import React, { ReactElement, useEffect, useState } from 'react';
import { Alert, alpha, Box, Button, Paper, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import CustomDialog from 'components/CustomDialog';
import { openSpotDetail } from 'store/slices/spots';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import MapPlaceholder from './MapPlaceholder';
import Loading from 'components/Loading';
import theme, { styled } from 'theme';
import Handler from './Handler';
import Grid from '@mui/material/Unstable_Grid2';
import Carousel from 'react-material-ui-carousel';
import PictureInSpot from 'domain/PictureInSpot';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { renderCategoryIcon, renderHazardIcon } from 'utils/categoriesAndHazards';
import Hazards from '../../domain/Hazards';
import Category from '../../domain/Category';
import SpotSocialBar from 'components/SpotSocialBar';
import SpotComments from 'components/SpotComments';

const YoutubeCont = styled('div')(({ theme }) => ({
    margin: '0',
    position: 'relative',
    width: '100%',
    height: 0,
    paddingBottom: '56.25%',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
}));

const Video = styled('iframe')({
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: 0,
    top: 0,
});

interface SpotDetailRouteParams {
    id: string;
}

const Item = ({ item }: { item: PictureInSpot }) => (
    <Paper
        sx={{
            height: 200,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 3,
            boxShadow: 'none',
        }}
    >
        <img
            src={item.mediumUrl}
            alt={item.name}
            style={{
                borderRadius: theme.shape.borderRadius,
                maxHeight: '100%',
                maxWidth: '100%',
            }}
        />
    </Paper>
);

interface CustomAlertProps {
    c: Category | Hazards,
    severity: 'error' | 'success' | 'info' | 'warning',
    renderIconFn: (shortname: string) => ReactElement | null
}

const CustomAlert = ({ c, severity, renderIconFn }: CustomAlertProps) => {
    return <Alert severity={severity} icon={renderIconFn(c.shortname)}>
        <Typography
            variant={'h6'}
            sx={{
                mb: .5
            }}
        >
            {c.name}
        </Typography>
        <Typography
            variant={'body2'}
        >
            {c.description}
        </Typography>
    </Alert>
}

const SpotDetail = () => {
    const { id: idSpot } = useParams<SpotDetailRouteParams>();
    const dispatch = useAppDispatch();
    const spotsData = useAppSelector((state) => state.spots);
    const [selectedSection, setSelectedSection] = useState<number | null>(null);
    const { openingSpotDetail, spotDetailError, spotDetailSuccess, spotDetailData: spot } = spotsData;

    useEffect(() => {
        if (idSpot) {
            void dispatch(openSpotDetail({ idSpot }));
        }
    }, []);

    useEffect(() => {
        if (spot && selectedSection === null) {
            if (spot.youTubeId) setSelectedSection(0);
            else if (spot.pictures?.length) setSelectedSection(1);
            else if (spot.categories?.length || spot.hazards?.length) setSelectedSection(2);
        }
    }, [spot, selectedSection]);

    const handleSectionChange = (section: number) => {
        setSelectedSection(section);
    };

    const showHandler =
        spot?.user &&
        (spot.user.firstname ||
            spot.user.lastname ||
            spot.user.picture?.medium?.url ||
            (spot.user.availableSocialNetworks && spot.user.availableSocialNetworks.length > 0) ||
            (spot.user.customSocialNetworks && spot.user.customSocialNetworks.length > 0));

    return (
        <CustomDialog
            title={spot?.name ?? 'Spot Detail'}
            alternative={
                spot?.description ? (
                    <>
                        <Typography variant={'caption'}>{spot.description}</Typography>
                    </>
                ) : undefined
            }
            processing={openingSpotDetail}
            processingCopy={'Loading spot...'}
            error={spotDetailError}
            errorContent={
                <>
                    <Typography variant={'subtitle1'}>There was an error retrieving the spot.</Typography>
                    <Typography variant={'body1'}>Please try again later</Typography>
                </>
            }
            success={spotDetailSuccess}
            successUsesMain={true}
        >
            {!openingSpotDetail && spot ? (
                <Box sx={{ width: '100%' }}>
                    <Grid container spacing={1}>
                        <Grid xs={12} sm={showHandler ? 8 : 12}>
                            <MapPlaceholder />
                        </Grid>
                        {showHandler && (
                            <Grid xs={12} sm={4}>
                                <Box>
                                    <Handler idUser={spot.idUser} user={spot.user} />
                                </Box>
                            </Grid>
                        )}
                        <Grid xs={12}>
                            <Box
                                sx={{
                                    width: '100%',
                                    display: 'block',
                                    textAlign: 'center',
                                }}
                            >
                                {spot.youTubeId ?
                                    <Button
                                        sx={{
                                            margin: `0 ${theme.spacing(2)}`,
                                        }}
                                        size={'small'}
                                        variant={selectedSection === 0 ? 'contained' : 'outlined'}
                                        onClick={() => handleSectionChange(0)}
                                    >
                                        Video
                                    </Button> : null}
                                {spot.pictures && spot.pictures.length ?
                                    <Button
                                        sx={{
                                            margin: `0 ${theme.spacing(2)}`,
                                        }}
                                        size={'small'}
                                        variant={selectedSection === 1 ? 'contained' : 'outlined'}
                                        onClick={() => handleSectionChange(1)}
                                    >
                                        Photos
                                    </Button> : null}
                                {spot.categories || spot.hazards ?
                                    <Button
                                        sx={{
                                            margin: `0 ${theme.spacing(2)}`,
                                        }}
                                        size={'small'}
                                        variant={selectedSection === 2 ? 'contained' : 'outlined'}
                                        onClick={() => handleSectionChange(2)}
                                    >
                                        Details
                                    </Button> : null}
                            </Box>
                        </Grid>
                    </Grid>
                    {selectedSection === 0 && spot.youTubeId ? (
                        <Box sx={{ pt: 2 }}>
                            <YoutubeCont>
                                <Video
                                    frameBorder='0'
                                    scrolling='no'
                                    marginHeight={0}
                                    marginWidth={0}
                                    src={`https://www.youtube.com/embed/${spot.youTubeId}?autoplay=0`}
                                />
                            </YoutubeCont>
                        </Box>
                    ) : null}
                    {selectedSection === 1 && spot.pictures && spot.pictures.length ? (
                        <Box
                            sx={{
                                border: 'none',
                            }}
                        >
                            <Carousel
                                navButtonsAlwaysVisible={true}
                                indicators={false}
                                navButtonsProps={{
                                    style: {
                                        backgroundColor: theme.palette.background.default,
                                        color: theme.palette.primary.main,
                                        width: 45,
                                        height: 45,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                                    },
                                }}
                                NextIcon={<ChevronRight/>}
                                PrevIcon={<ChevronLeft/>}
                            >
                                {spot.pictures.map((item, i) => (
                                    <Item key={i} item={item}/>
                                ))}
                            </Carousel>
                        </Box>
                    ) : null}
                    {selectedSection === 2 && (spot.categories || spot.hazards) ? (
                        <Grid container py={2} spacing={1}>
                            {spot.categories
                                ? spot.categories.map((c) => (
                                    <Grid key={c.name} xs={12} sm={6}>
                                        <Box display='flex' alignItems='stretch' height='100%'>
                                            <CustomAlert
                                                severity={'success'}
                                                c={c}
                                                renderIconFn={renderCategoryIcon}
                                            />
                                        </Box>
                                    </Grid>
                                ))
                                : null}
                            {spot.hazards
                                ? spot.hazards.map((h) => (
                                    <Grid key={h.name} xs={12} sm={6}>
                                        <Box display='flex' alignItems='stretch' height='100%'>
                                            <CustomAlert
                                                severity={'warning'}
                                                c={h}
                                                renderIconFn={renderHazardIcon}
                                            />
                                        </Box>
                                    </Grid>
                                ))
                                : null}
                        </Grid>
                    ) : null}
                    <Box mt={1}>
                        <SpotSocialBar spot={spot} />
                    </Box>
                    {spot.id && <SpotComments idSpot={spot.id} />}
                </Box>
            ) : (
                <Loading text={'Loading spot...'}/>
            )}
        </CustomDialog>
    );
};

export default SpotDetail;
